import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { LEAD_STATUS, LEAD_STATUS_ORDER, PLAN_OPTIONS } from "../../lib/b2b";
import { ArrowLeft, Loader2, Upload, FileText, Clock, StickyNote, Save } from "lucide-react";

const Info = ({ label, value }) => (
  <div><p className="text-xs font-semibold text-slate-400">{label}</p><p className="text-sm text-slate-800">{value || "—"}</p></div>
);

export default function AdminLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/b2b/leads/${id}`);
    setLead(data);
    setEdit({ status: data.status, assigned_to: data.assigned_to || "", next_contact_date: data.next_contact_date || "", proposed_plan: data.proposed_plan || "", proposed_units: data.proposed_units || "", lost_reason: data.lost_reason || "" });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload, msg) => {
    setBusy(true);
    try { const { data } = await api.patch(`/admin/b2b/leads/${id}`, payload); setLead(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const saveFields = () => patch(edit, "Scheda aggiornata");
  const addNote = async () => { if (!note.trim()) return; await patch({ note }, "Nota aggiunta"); setNote(""); };

  const uploadProposal = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("files", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await patch({ proposal_url: data.urls[0] }, "Proposta caricata");
    } catch { toast.error("Caricamento non riuscito"); } finally { setBusy(false); }
  };

  if (!lead) return <div className="flex min-h-screen items-center justify-center bg-slate-100"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C7B]" /></div>;
  const st = LEAD_STATUS[lead.status] || LEAD_STATUS.nuovo;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-slate-700 bg-slate-900 px-4 py-3 text-white sm:px-8">
        <button data-testid="lead-back" onClick={() => nav("/centrale/lead")} className="rounded-full bg-slate-800 p-2"><ArrowLeft className="h-4 w-4" /></button>
        <p className="font-display text-sm font-extrabold">{lead.code}</p>
        <span className={`ml-auto rounded-full border px-2.5 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
      </header>

      <div className="mx-auto grid max-w-5xl gap-5 p-4 sm:p-8 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-lg font-extrabold text-slate-900">{lead.name}</h2>
            <p className="text-sm text-slate-500">{lead.company || "Privato"} · {lead.role || "ruolo n/d"}</p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Info label="Email" value={lead.email} /><Info label="Telefono" value={lead.phone} />
              <Info label="Unità" value={lead.units} /><Info label="Tipologia" value={lead.property_type} />
              <Info label="Zone / CAP" value={lead.zones} /><Info label="Metratura" value={lead.size_range} />
              <Info label="Split / unità" value={lead.splits_per_unit} /><Info label="Piano interesse" value={lead.plan_interest} />
              <Info label="Fascia ricontatto" value={lead.preferred_time} /><Info label="Camere" value={lead.rooms} />
              <Info label="Spazi comuni" value={lead.common_areas} />
            </div>
            <div className="mt-4 space-y-3">
              <Info label="Gestione attuale" value={lead.current_management} />
              <Info label="Problemi principali" value={lead.main_problems} />
              <Info label="Messaggio" value={lead.message} />
              {lead.selector_answers && <Info label="Risposte selettore" value={JSON.stringify(lead.selector_answers)} />}
              <p className="text-xs text-slate-400">Marketing: {lead.marketing_consent ? "acconsente" : "no"} · Privacy: {lead.privacy_consent ? "accettata" : "—"}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="mb-2 flex items-center gap-2 font-bold text-slate-800"><StickyNote className="h-4 w-4 text-[#0E7C7B]" />Note e attività</p>
            <div className="space-y-2">
              {(lead.notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700"><p>{n.text}</p><p className="mt-1 text-xs text-slate-400">{n.by} · {new Date(n.at).toLocaleString("it-IT")}</p></div>
              ))}
              {(lead.notes || []).length === 0 && <p className="text-sm text-slate-400">Nessuna nota.</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota…" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none" />
              <button data-testid="lead-note-add" onClick={addNote} disabled={busy} className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Aggiungi</button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="mb-2 flex items-center gap-2 font-bold text-slate-800"><Clock className="h-4 w-4 text-[#0E7C7B]" />Storico</p>
            {(lead.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 py-1.5 text-sm last:border-0"><span className="font-semibold">{LEAD_STATUS[h.status]?.label || h.status}</span><span className="text-xs text-slate-400">{new Date(h.at).toLocaleString("it-IT")}</span></div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="mb-3 font-bold text-slate-800">Gestione commerciale</p>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Stato</label>
            <select data-testid="lead-status-select" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {LEAD_STATUS_ORDER.map((s) => <option key={s} value={s}>{LEAD_STATUS[s].label}</option>)}
            </select>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Assegnato a</label>
            <input data-testid="lead-assigned" value={edit.assigned_to} onChange={(e) => setEdit({ ...edit, assigned_to: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <label className="mb-1 block text-xs font-semibold text-slate-500">Prossimo contatto</label>
            <input data-testid="lead-next-contact" type="date" value={edit.next_contact_date} onChange={(e) => setEdit({ ...edit, next_contact_date: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <label className="mb-1 block text-xs font-semibold text-slate-500">Piano proposto</label>
            <select value={edit.proposed_plan} onChange={(e) => setEdit({ ...edit, proposed_plan: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="">—</option>{PLAN_OPTIONS.filter((p) => p.v !== "aiuto").map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Unità (proposta)</label>
            <input value={edit.proposed_units} onChange={(e) => setEdit({ ...edit, proposed_units: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            {edit.status === "perso" && (<><label className="mb-1 block text-xs font-semibold text-slate-500">Motivo di perdita</label><input data-testid="lead-lost-reason" value={edit.lost_reason} onChange={(e) => setEdit({ ...edit, lost_reason: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></>)}
            <button data-testid="lead-save" onClick={saveFields} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0E7C7B] py-2.5 text-sm font-bold text-white disabled:opacity-50"><Save className="h-4 w-4" />Salva</button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="mb-2 font-bold text-slate-800">Proposta commerciale</p>
            {lead.proposal_url ? <a href={lead.proposal_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#0E7C7B]"><FileText className="h-4 w-4" />Apri proposta caricata</a> : <p className="text-sm text-slate-400">Nessuna proposta caricata.</p>}
            <label data-testid="lead-upload" className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-2.5 text-sm font-semibold text-slate-600">
              <Upload className="h-4 w-4" />{busy ? "Caricamento…" : "Carica proposta (PDF)"}
              <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={uploadProposal} className="hidden" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
